<template>
  <div class="quotes-list">
    <div class="search-bar">
      <div class="search-wrapper">
        <i class="mdi mdi-magnify search-icon"></i>
        <input
          type="text"
          v-model="search"
          :placeholder="$t('message.quotes_page.search_placeholder')"
          class="search-input"
          @keyup.enter="loadQuotes(1)"
        />
        <button
          v-if="search"
          @click="
            search = '';
            loadQuotes(1);
          "
          class="clear-search"
        >
          <i class="mdi mdi-close"></i>
        </button>
      </div>

      <select v-model="status" class="status-select" @change="loadQuotes(1)">
        <option value="open">{{
          $t("message.quotes_page.status_open")
        }}</option>
        <option value="converted">
          {{ $t("message.quotes_page.status_converted") }}
        </option>
        <option value="all">{{ $t("message.quotes_page.status_all") }}</option>
      </select>

      <!-- A customer's quotes are shared by every seller -->
      <div v-if="!forCustomer && canViewOtherSellers" class="switch-wrapper">
        <span>{{ $t("message.quotes_page.only_mine") }}</span>
        <label class="custom-switch">
          <input type="checkbox" v-model="onlyMine" @change="loadQuotes(1)" />
          <span class="slider"></span>
        </label>
      </div>

      <button class="btn-success-button new-quote-btn" @click="openNewForm">
        <i class="mdi mdi-plus"></i>
        {{ $t("message.quotes_page.new_quote") }}
      </button>
    </div>

    <div v-if="showNewForm" class="new-quote-form">
      <input
        ref="newNameInput"
        v-model="newName"
        type="text"
        class="default-input"
        maxlength="120"
        :placeholder="$t('message.quotes_page.new_quote_name_placeholder')"
        @keyup.enter="create"
        @keyup.esc="showNewForm = false"
      />
      <button class="btn-success-button" :disabled="creating" @click="create">
        {{
          creating
            ? $t("message.quotes_page.creating")
            : $t("message.quotes_page.create")
        }}
      </button>
      <button class="btn-dark-button" @click="showNewForm = false">
        {{ $t("message.quotes_page.cancel") }}
      </button>
    </div>

    <div class="header">
      <BaseMiniSpinner v-if="loading" class="mr-2" />
      <div>{{ title || $t("message.quotes_page.title") }}</div>
    </div>

    <div v-if="!loading && !quotes.length" class="empty">
      {{ $t("message.quotes_page.no_quotes") }}
    </div>

    <!-- Desktop Table -->
    <div v-if="quotes.length" class="hidden lg:block table-wrapper">
      <table class="default-table">
        <thead>
          <tr>
            <th>{{ $t("message.quotes_page.quote_number") }}</th>
            <th>{{ $t("message.quotes_page.name") }}</th>
            <th v-if="!forCustomer">
              {{ $t("message.quotes_page.customer") }}
            </th>
            <th>{{ $t("message.quotes_page.items") }}</th>
            <th>{{ $t("message.quotes_page.total") }}</th>
            <th>{{ $t("message.quotes_page.created_by") }}</th>
            <th>{{ $t("message.quotes_page.date") }}</th>
            <th>{{ $t("message.quotes_page.status") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="quote in quotes"
            :key="quote.quoteNumber"
            class="clickable"
            @click="open(quote)"
          >
            <td class="font-semibold">Q-{{ quote.quoteNumber }}</td>
            <td>
              <BaseTooltip :text="quote.quoteName" :disabled="!quote.quoteName">
                {{ quote.quoteName || "-" }}
              </BaseTooltip>
            </td>
            <td v-if="!forCustomer">
              <span v-if="quote.customerName">{{ quote.customerName }}</span>
              <span v-else class="text-gray-400">
                {{ $t("message.quotes_page.no_customer") }}
              </span>
            </td>
            <td>{{ quote.productsLines || 0 }}</td>
            <td>{{ quote.total | currencyFormatted(true) }}</td>
            <td>{{ quote.sellerName || quote.salesman || "-" }}</td>
            <td>{{ quote.createDate | dateWithHours }}</td>
            <td>
              <span
                v-if="!quote.convertedOrderNumber && quote.readyAt"
                class="status-pill is-ready"
              >
                <i class="mdi mdi-account-check-outline"></i>
                {{ $t("message.quotes_page.status_ready") }}
              </span>
              <span
                v-else-if="!quote.convertedOrderNumber"
                class="status-pill is-open"
              >
                {{ $t("message.quotes_page.status_open") }}
              </span>
              <span v-else class="status-pill is-converted">
                <i class="mdi mdi-check-circle"></i>
                {{
                  $t("message.quotes_page.converted_to", {
                    order: quote.convertedOrderNumber
                  })
                }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Mobile Card View -->
    <div v-if="quotes.length" class="mobile-cards lg:hidden">
      <div
        v-for="quote in quotes"
        :key="quote.quoteNumber"
        class="quote-card"
        @click="open(quote)"
      >
        <div class="card-header">
          <span class="font-bold">Q-{{ quote.quoteNumber }}</span>
          <span class="font-bold text-green-600">
            {{ quote.total | currencyFormatted(true) }}
          </span>
        </div>
        <div class="info-row" v-if="quote.quoteName">
          <i class="mdi mdi-tag text-gray-500"></i>
          <span>{{ quote.quoteName }}</span>
        </div>
        <div class="info-row" v-if="!forCustomer">
          <i class="mdi mdi-account text-gray-500"></i>
          <span>{{
            quote.customerName || $t("message.quotes_page.no_customer")
          }}</span>
        </div>
        <div class="info-row">
          <i class="mdi mdi-calendar text-gray-500"></i>
          <span>{{ quote.createDate | dateWithHours }}</span>
        </div>
        <div class="info-row">
          <i class="mdi mdi-package-variant text-gray-500"></i>
          <span
            >{{ quote.productsLines || 0 }}
            {{ $t("message.quotes_page.items") }}</span
          >
        </div>
        <div
          class="info-row"
          v-if="!quote.convertedOrderNumber && quote.readyAt"
        >
          <i class="mdi mdi-account-check-outline text-blue-600"></i>
          <span>{{ $t("message.quotes_page.status_ready") }}</span>
        </div>
        <div class="info-row" v-if="quote.convertedOrderNumber">
          <i class="mdi mdi-check-circle text-green-600"></i>
          <span>{{
            $t("message.quotes_page.converted_to", {
              order: quote.convertedOrderNumber
            })
          }}</span>
        </div>
      </div>
    </div>

    <div v-if="totalItems > perPage" class="footer">
      <div>
        {{ $t("message.quotes_page.showing") }}
        {{ (page - 1) * perPage + 1 }}
        {{ $t("message.quotes_page.to") }}
        {{ Math.min(page * perPage, totalItems) }}
        {{ $t("message.quotes_page.from") }}
        {{ totalItems }}
        {{ $t("message.quotes_page.results") }}
      </div>
      <div class="pages">
        <a
          class="page-link"
          :class="{ disabled: page <= 1 }"
          @click="loadQuotes(page - 1)"
          >{{ $t("message.quotes_page.previous") }}</a
        >
        <div>{{ $t("message.quotes_page.page") }} {{ page }}</div>
        <a
          class="page-link"
          :class="{ disabled: page >= totalPages }"
          @click="loadQuotes(page + 1)"
          >{{ $t("message.quotes_page.next") }}</a
        >
      </div>
    </div>
  </div>
</template>

<script>
import { getQuotes, createQuote } from "@/api/store/QuotesService.js";

// Quotes list, for the whole store (quotes section of OrdersPage2) or for one customer: infoauto
// (customerInfoautoId) or web (customerEmail), from their customer modal.
// A converted quote stays as history (convertedOrderNumber) and opens read-only. Opening a quote is up to the parent: it emits openQuote
// with the quote's (negative) order number, or the order number once it was
// converted.
export default {
  name: "QuotesList",
  props: {
    customerInfoautoId: {
      type: String,
      default: ""
    },
    customerEmail: {
      type: String,
      default: ""
    },
    title: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      quotes: [],
      search: "",
      status: "open",
      onlyMine: true,
      page: 1,
      perPage: 50,
      totalItems: 0,
      totalPages: 0,
      loading: false,
      showNewForm: false,
      newName: "",
      creating: false
    };
  },
  computed: {
    forCustomer() {
      return !!(this.customerInfoautoId || this.customerEmail);
    },
    // Sin el permiso el API sólo devuelve las cotizaciones del propio vendedor.
    canViewOtherSellers() {
      return this.$store.getters["auth/userHasPermission"](
        "can_view_orders_other_sellers"
      );
    }
  },
  watch: {
    customerInfoautoId() {
      this.loadQuotes(1);
    },
    customerEmail() {
      this.loadQuotes(1);
    }
  },
  created() {
    this.loadQuotes();
  },
  methods: {
    async loadQuotes(newPage) {
      if (newPage !== undefined) {
        if (newPage < 1) return;
        this.page = newPage;
      }
      this.loading = true;
      try {
        const result = await getQuotes({
          page: this.page,
          limitPerPage: this.perPage,
          search: this.search,
          status: this.status,
          showAll: !this.onlyMine,
          customerInfoautoId: this.customerInfoautoId,
          customerEmail: this.customerInfoautoId ? "" : this.customerEmail
        });
        this.quotes = result.quotes || [];
        this.totalItems = Number(result.totalItems) || 0;
        this.totalPages = Number(result.totalPages) || 0;
      } catch (e) {
        this.$toast.open({
          message: this.$t("message.quotes_page.error_loading"),
          type: "error",
          position: "top-right"
        });
      } finally {
        this.loading = false;
      }
    },
    // Called by the parent after the quote modal changes something
    reload() {
      return this.loadQuotes();
    },
    open(quote) {
      this.$emit("openQuote", quote.orderNumber);
    },
    openNewForm() {
      this.newName = "";
      this.showNewForm = true;
      this.$nextTick(
        () => this.$refs.newNameInput && this.$refs.newNameInput.focus()
      );
    },
    async create() {
      if (this.creating) return;
      this.creating = true;
      try {
        const result = await createQuote({
          customerInfoautoId: this.customerInfoautoId || null,
          customerEmail: this.customerInfoautoId
            ? null
            : this.customerEmail || null,
          name: this.newName.trim()
        });
        this.showNewForm = false;
        this.$toast.open({
          message: this.$t("message.quotes_page.quote_created", {
            quote: `Q-${result.quoteNumber}`
          }),
          type: "success",
          position: "top-right"
        });
        this.loadQuotes(1);
        this.$emit("openQuote", result.orderNumber);
      } catch (e) {
        this.$toast.open({
          message:
            e?.response?.data?.message ||
            this.$t("message.quotes_page.error_creating"),
          type: "error",
          position: "top-right"
        });
      } finally {
        this.creating = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.quotes-list {
  width: 100%;
  background: white;
}

.search-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;

  .search-wrapper {
    display: flex;
    align-items: center;
    flex: 1;
    min-width: 200px;
    max-width: 500px;
    position: relative;

    .search-icon {
      position: absolute;
      left: 12px;
      color: #9ca3af;
      font-size: 18px;
    }

    .search-input {
      width: 100%;
      padding: 10px 12px 10px 40px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      font-size: 14px;

      &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }
    }

    .clear-search {
      position: absolute;
      right: 8px;
      padding: 4px;
      border: none;
      background: none;
      cursor: pointer;
      color: #9ca3af;
    }
  }
}

.status-select {
  padding: 9px 10px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: white;
}

.switch-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #374151;
  white-space: nowrap;
}

.new-quote-btn {
  margin-left: auto;
  white-space: nowrap;
}

.new-quote-form {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 20px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;

  input {
    flex: 1;
    min-width: 200px;
    max-width: 420px;
  }
}

.header {
  display: flex;
  align-items: center;
  padding: 16px 20px;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  border-bottom: 1px solid #e5e7eb;
}

.empty {
  padding: 32px 20px;
  text-align: center;
  color: #9ca3af;
}

.table-wrapper {
  overflow-x: auto;
}

.default-table {
  width: 100%;
  border-collapse: collapse;

  thead th {
    padding: 12px 16px;
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    color: #6b7280;
    background: #f9fafb;
    border-bottom: 2px solid #e5e7eb;
    white-space: nowrap;
  }

  tbody td {
    padding: 12px 16px;
    font-size: 14px;
    color: #374151;
    border-bottom: 1px solid #f3f4f6;
    max-width: 240px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  tbody tr.clickable {
    cursor: pointer;

    &:hover {
      background-color: #f9fafb;
    }
  }
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;

  &.is-open {
    background: #ede9fe;
    color: #5b21b6;
  }

  &.is-ready {
    background: #dbeafe;
    color: #1e40af;
  }

  &.is-converted {
    background: #dcfce7;
    color: #166534;
  }
}

.mobile-cards {
  padding: 12px;

  .quote-card {
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 14px 16px;
    margin-bottom: 12px;
    cursor: pointer;

    .card-header {
      display: flex;
      justify-content: space-between;
      margin-bottom: 8px;
    }

    .info-row {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 3px 0;
      font-size: 13px;
      color: #4b5563;
    }
  }
}

.footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #e5e7eb;
  font-size: 14px;
  color: #6b7280;

  .pages {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .page-link {
    color: #3b82f6;
    cursor: pointer;
    user-select: none;

    &.disabled {
      color: #d1d5db;
      pointer-events: none;
    }
  }
}

.custom-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
}

.slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background-color: #e5e7eb;
  transition: 0.3s;
  border-radius: 24px;

  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
}

input:checked + .slider {
  background-color: #3b82f6;
}

input:checked + .slider:before {
  transform: translateX(20px);
}
</style>
